import { isPointInPolygon } from 'geolib';
import type { Coords } from 'google-map-react';

import type { DeliveryZone } from '~/api/DeliveryAddress';
import { company } from '~/company/Company';
import { DELIVERY_ZONES_STYLE } from '~/components/ModalDeliveryAddress/GMap/constants';
import { mainStore } from '~/stores';

import type { PickupPoint } from './interfaces';

/**
 * Stable identity of a pickup point for React keys and selection matching.
 * Pickup points are collapsed to one per warehouse (see selectPickupPoints), so
 * the warehouse code alone is a unique, stable id.
 */
export function getPickupPointId(point: PickupPoint): string {
  return point.warehouseCode;
}

/**
 * Tolerance for {@link isSameCoordinate}: ~1 cm, far below anything a drag or a
 * geolocation fix produces and far above the rounding the map API introduces
 * when it hands a center back through its own projection.
 */
const SAME_COORDINATE_EPSILON = 1e-7;

/** Whether two points are the same place on the map. */
export function isSameCoordinate(
  a: GeoCoordinates,
  b: GeoCoordinates,
): boolean {
  return (
    Math.abs(a.lat - b.lat) < SAME_COORDINATE_EPSILON &&
    Math.abs(a.lng - b.lng) < SAME_COORDINATE_EPSILON
  );
}

/**
 * Checks whether a point falls inside a delivery zone polygon.
 * Google Maps relies on its geometry library, while Yandex Maps zones store
 * raw ring coordinates and are tested with a provider-independent algorithm.
 */
export const isInsideZone = (position: Coords, polygon: any): boolean => {
  if (company.isYandexMaps) {
    return isPointInPolygon(position, polygon as GeoCoordinates[]);
  }

  const result = google.maps.geometry?.poly.containsLocation(
    new google.maps.LatLng(position.lat, position.lng),
    polygon,
  );

  return Boolean(result);
};

export const getPolygonCoords = (zone: DeliveryZone) =>
  mainStore.flatArray(
    zone.polygon.segments.map((segment) =>
      Object.values(segment).map((coord) => ({
        lat: coord.latitude,
        lng: coord.longitude,
      })),
    ),
  );

export const createPolygonForZone = (
  zone: DeliveryZone,
): google.maps.Polygon => {
  const convert = getPolygonCoords(zone);
  const isPudo = zone.displayType === 'pudo';

  const style =
    isPudo || company.zonePolygonHidden
      ? { strokeOpacity: 0, fillOpacity: 0 }
      : DELIVERY_ZONES_STYLE[zone.priority as number] ||
        DELIVERY_ZONES_STYLE[100];

  return new google.maps.Polygon({
    paths: convert,
    strokeWeight: 1,
    fillColor: zone.color,
    strokeColor: zone.color,
    // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
    fillOpacity: style.fillOpacity,
    // @ts-expect-error FIXME: migrate to noUncheckedIndexedAccess: true
    strokeOpacity: style.strokeOpacity,
  });
};

export const getZoneCentroid = (zone: DeliveryZone): Coords => {
  const coords = getPolygonCoords(zone);
  return {
    lat: coords.reduce((sum, c) => sum + c.lat, 0) / coords.length,
    lng: coords.reduce((sum, c) => sum + c.lng, 0) / coords.length,
  };
};
